// ============================================================================
// LandingDesignPanel — admin toggle between V1 (original snap-chart.ai) and
// V3 (2026 viral redesign). Controls the WHOLE site: landing, navbar,
// dashboard, login, register. One setting, one switch, instant rollback.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiExternalLink, FiAlertCircle, FiZap, FiTrendingUp, FiShield, FiTarget, FiCpu, FiShare2, FiSmartphone } = FiIcons;

type Version = 'v1' | 'v3';

type Option = {
  id: Version;
  title: string;
  tagline: string;
  whatChanges: string;
  features: { icon: any; label: string; sub: string }[];
};

const OPTIONS: Option[] = [
  {
    id: 'v1',
    title: 'Original (snap-chart.ai)',
    tagline: 'The classic long-form design — institutional, dense, mature.',
    whatChanges: 'Reverts the landing page, navbar, and dashboard to the original snap-chart.ai design used since launch.',
    features: [
      { icon: FiTrendingUp,  label: 'Long-scroll landing',          sub: 'Full feature pitch, dense info, every section explained' },
      { icon: FiShield,      label: 'Marketing-anchor navbar',      sub: 'What / Why / How / Features / Demo / Pricing / FAQ / Contact links' },
      { icon: FiCpu,         label: 'Original dashboard',           sub: 'Platform Status card, Launch SnapChart CTA, classic chip buttons' },
      { icon: FiTarget,      label: 'No TDI hero on dashboard',     sub: 'TDI lives in the Behavior & Discipline modal only' },
    ],
  },
  {
    id: 'v3',
    title: '2026 Viral (cyan/purple)',
    tagline: 'New design — tighter, share-built-in, $1B-startup look.',
    whatChanges: 'Switches the entire site (landing + navbar + dashboard + login + register) to the 2026 redesign.',
    features: [
      { icon: FiZap,         label: 'New landing page',            sub: 'Slim navbar, big hero, founder note, "what we are NOT", category vision' },
      { icon: FiTrendingUp,  label: 'TDI hero on dashboard',       sub: 'FICO-style 300-850 score, tier badge, trend, radial gauge (real data)' },
      { icon: FiTarget,      label: 'Discipline streak (Duolingo)', sub: 'Real consecutive-day counter from journal entries · "don\'t break it"' },
      { icon: FiCpu,         label: 'Latest verdicts block',       sub: 'Your last 3 AI verdicts with verdict-color tokens (real data)' },
      { icon: FiShare2,      label: '1080×1080 share cards',       sub: 'Hover any verdict to share as a branded square (X / IG / TikTok / WhatsApp / Copy)' },
      { icon: FiSmartphone,  label: 'Mobile-first nav',            sub: 'Hamburger drawer, scroll-lock, big touch targets, context-aware auth/public modes' },
      { icon: FiShield,      label: 'New login + register',        sub: 'Split-screen with live stats teaser on the left, form on the right' },
      { icon: FiZap,         label: '/test free calculator',       sub: 'Public no-signup viral hook: "how much would discipline save you?"' },
    ],
  },
];

const LandingDesignPanel: React.FC = () => {
  const [current, setCurrent] = useState<Version | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Version | null>(null);

  const load = async () => {
    try {
      const { data, error: e } = await supabase.rpc('get_landing_design_version');
      if (e) throw e;
      const v = (typeof data === 'string' ? data : data?.version) as Version;
      setCurrent(v === 'v1' || v === 'v3' ? v : 'v3');
    } catch (err: any) {
      setError(err?.message || 'Failed to load current setting');
      setCurrent('v3');
    }
  };

  useEffect(() => { load(); }, []);

  const apply = async (version: Version) => {
    setLoading(true);
    setError(null);
    setSaved(null);
    try {
      const { data, error: e } = await supabase.rpc('set_landing_design_version', { p_version: version });
      if (e) throw e;
      if (!data?.success) throw new Error(data?.error || 'Update failed');
      setCurrent(version);
      setSaved(version);
      try { localStorage.setItem('landing_design_version_cache', version); } catch { /* noop */ }
      setTimeout(() => setSaved(null), 4000);
    } catch (err: any) {
      setError(err?.message || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Site Design</h2>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            One toggle controls the whole site theme — landing page, navbar, dashboard, login, and register.
            Switch instantly. Roll back anytime. New visitors see the change immediately;
            cached users on next page load.
          </p>
        </div>
        <a href="/" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 text-sm transition">
          <SafeIcon icon={FiExternalLink} /> Open live site
        </a>
      </div>

      {/* Status banners */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-200">
          <SafeIcon icon={FiAlertCircle} /> {error}
        </div>
      )}
      {saved && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-sm text-emerald-200">
          <SafeIcon icon={FiCheck} /> Site is now serving <strong>{saved.toUpperCase()}</strong> design to every visitor.
        </div>
      )}

      {/* Option cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {OPTIONS.map((opt) => {
          const isCurrent = current === opt.id;
          return (
            <div key={opt.id}
              className={`relative rounded-2xl border p-6 transition ${
                isCurrent
                  ? 'border-cyan-400/50 bg-gradient-to-br from-cyan-500/10 via-gray-900/40 to-purple-500/10 shadow-xl shadow-cyan-500/10'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/20'
              }`}>
              {isCurrent && (
                <div className="absolute -top-2 -right-2 px-2.5 py-0.5 rounded-md bg-cyan-400 text-gray-900 text-[10px] font-mono uppercase tracking-[0.15em] font-bold">
                  <SafeIcon icon={FiZap} className="inline mr-1 text-xs" /> live now
                </div>
              )}

              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">design · {opt.id}</span>
                {isCurrent && <span className="text-[10px] font-mono uppercase tracking-wider text-cyan-400">selected</span>}
              </div>

              <h3 className="text-lg font-bold text-white mb-1">{opt.title}</h3>
              <p className="text-sm text-gray-400 mb-3">{opt.tagline}</p>
              <p className="text-xs text-gray-500 mb-4 italic">{opt.whatChanges}</p>

              <ul className="space-y-2 mb-5">
                {opt.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      opt.id === 'v3' ? 'bg-cyan-500/10 text-cyan-400' : 'bg-white/[0.04] text-gray-400'
                    }`}>
                      <SafeIcon icon={f.icon} className="text-xs" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white leading-tight">{f.label}</div>
                      <div className="text-xs text-gray-400 leading-snug mt-0.5">{f.sub}</div>
                    </div>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => apply(opt.id)}
                disabled={loading || isCurrent}
                className={`w-full py-2.5 rounded-lg font-bold text-sm transition ${
                  isCurrent
                    ? 'bg-white/5 text-gray-500 border border-white/5 cursor-default'
                    : 'bg-gradient-to-r from-cyan-400 to-purple-400 text-black hover:scale-[1.01] shadow-lg shadow-cyan-500/20'
                } disabled:opacity-60`}
              >
                {isCurrent ? '✓ Currently live' : loading ? 'Saving...' : `Switch to ${opt.id.toUpperCase()}`}
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div className="text-xs text-gray-500 font-mono leading-relaxed pt-4 border-t border-white/5">
        <p className="mb-1"><strong className="text-gray-400">How it works:</strong> the switcher uses a single setting in the
        <code className="px-1 mx-1 rounded bg-white/5 text-cyan-300">landing_design_setting</code> table. Three switcher components
        (<code className="px-1 mx-1 rounded bg-white/5 text-cyan-300">LandingPage</code>, <code className="px-1 mx-1 rounded bg-white/5 text-cyan-300">Navbar</code>, <code className="px-1 mx-1 rounded bg-white/5 text-cyan-300">Dashboard</code>)
        read it via the <code className="px-1 mx-1 rounded bg-white/5 text-cyan-300">useDesignVersion()</code> hook and render either the V1 or V3 child component.</p>
        <p>Reverting is instant — never a destructive change.</p>
      </div>
    </div>
  );
};

export default LandingDesignPanel;
